import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, CheckCircle2, Repeat, TriangleAlert, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEKDAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const weekdayOfIso = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  // JS getDay: 0=Sun..6=Sat. Convert to Mon=0..Sun=6.
  const js = d.getDay();
  return WEEKDAY_ORDER[(js + 6) % 7];
};

const formatIsoDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Repeat Sessions Wizard (Phase 85.3).
 *
 * Props:
 *   open              — bool
 *   onOpenChange(v)   — dialog controller
 *   sourceSessions    — array of already-selected training session objects
 *   onGenerated(n)    — callback fired after successful generation
 */
export default function RepeatSessionsWizard({ open, onOpenChange, sourceSessions = [], onGenerated }) {
  const [step, setStep] = useState(1);
  const [frequency, setFrequency] = useState('weekly');
  const [endDate, setEndDate] = useState('');
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [excludeDatesInput, setExcludeDatesInput] = useState('');
  const [excludeDates, setExcludeDates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setStep(1);
      setFrequency('weekly');
      setEndDate('');
      const srcWeekdays = Array.from(new Set(sourceSessions.map(s => weekdayOfIso(s.date)).filter(Boolean)));
      setSelectedWeekdays(srcWeekdays);
      setSkipWeekends(false);
      setExcludeDatesInput('');
      setExcludeDates([]);
      setPreview(null);
      setPreviewError(null);
    }
  }, [open, sourceSessions]);

  const sourceWeekdayLabels = useMemo(() => {
    const days = Array.from(new Set(sourceSessions.map(s => weekdayOfIso(s.date)).filter(Boolean)));
    return days.join(', ');
  }, [sourceSessions]);

  const canGoStep2 = sourceSessions.length > 0;
  const canGoStep3 = Boolean(endDate) && (frequency !== 'weekly' || selectedWeekdays.length > 0);

  const toggleWeekday = (wd) => {
    setSelectedWeekdays(prev => (prev.includes(wd) ? prev.filter(w => w !== wd) : [...prev, wd]));
  };

  const addExcludeDate = () => {
    const v = excludeDatesInput.trim();
    if (!v) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      toast.error('Enter dates as YYYY-MM-DD');
      return;
    }
    if (excludeDates.includes(v)) return;
    setExcludeDates(prev => [...prev, v]);
    setExcludeDatesInput('');
  };

  const removeExcludeDate = (v) => setExcludeDates(prev => prev.filter(x => x !== v));

  const buildPayload = ({ confirm = false } = {}) => ({
    source_session_ids: sourceSessions.map(s => s.id),
    frequency,
    end_date: endDate,
    weekdays: frequency === 'weekly' ? selectedWeekdays : [],
    exclude_dates: excludeDates,
    skip_weekends: skipWeekends,
    confirm_soft_warning: confirm,
  });

  const fetchPreview = async () => {
    if (!canGoStep3) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const { data } = await axios.post(`${API}/training-sessions/repeat/preview`, buildPayload());
      setPreview(data);
      setStep(3);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPreviewError(typeof detail === 'string' ? detail : (detail?.message || 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runGenerate = async () => {
    if (!preview) return;
    if (preview.exceeds_hard_limit) {
      toast.error(`Refusing to generate ${preview.total_to_create} sessions in one request (hard limit ${preview.hard_limit}).`);
      return;
    }
    if (preview.requires_confirmation) {
      const ok = window.confirm(
        `You are about to create ${preview.total_to_create} sessions across ${preview.per_source.length} source session(s). Continue?`
      );
      if (!ok) return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/training-sessions/repeat/generate`,
        buildPayload({ confirm: true })
      );
      const created = data.created_count || 0;
      const failures = data.failures?.length || 0;
      if (created > 0) {
        toast.success(`Generated ${created} session${created === 1 ? '' : 's'}${failures ? ` · ${failures} skipped` : ''}`);
      } else if (failures > 0) {
        toast.warning(`No new sessions created — ${failures} skipped as duplicates or failed inserts`);
      } else {
        toast.info('No new sessions to create');
      }
      onGenerated?.(created);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Generation failed');
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="repeat-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="w-5 h-5 text-emerald-600" />
            Repeat Sessions
          </DialogTitle>
          <DialogDescription>
            Duplicate the selected sessions across future dates. Attendance marks, rosters and completion state are never carried over.
          </DialogDescription>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-2 text-[11px]" data-testid="repeat-wizard-steps">
          {[
            { n: 1, label: 'Selected' },
            { n: 2, label: 'Pattern' },
            { n: 3, label: 'Preview' },
          ].map(({ n, label }, idx) => (
            <React.Fragment key={n}>
              <div className={`flex items-center gap-1.5 ${step === n ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-gray-400'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === n ? 'bg-emerald-600 text-white' : step > n ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                  {step > n ? '✓' : n}
                </span>
                {label}
              </div>
              {idx < 2 && <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1 — Selected Sessions */}
        {step === 1 && (
          <div className="space-y-3 py-2" data-testid="repeat-wizard-step-1">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {sourceSessions.length} source session{sourceSessions.length === 1 ? '' : 's'} will be repeated:
            </p>
            <div className="max-h-64 overflow-y-auto space-y-1.5 border border-gray-200 dark:border-gray-700 rounded-md p-2">
              {sourceSessions.map(s => (
                <div key={s.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded" data-testid={`repeat-wizard-source-${s.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{s.title}</p>
                    <p className="text-gray-500">
                      {formatIsoDate(s.date)} · {s.time?.slice(0, 5)} · {s.session_type}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{weekdayOfIso(s.date)}</Badge>
                </div>
              ))}
              {sourceSessions.length === 0 && (
                <p className="text-xs text-gray-400 italic p-2">No sessions selected.</p>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 — Pattern */}
        {step === 2 && (
          <div className="space-y-4 py-2" data-testid="repeat-wizard-step-2">
            <div>
              <Label>Repeat pattern</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="repeat-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="weekdays">Weekdays (Mon–Fri)</SelectItem>
                </SelectContent>
              </Select>
              {frequency === 'weekly' && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Defaults to source session weekday{sourceWeekdayLabels ? ` (${sourceWeekdayLabels})` : ''}. Tick multiple days to override.
                </p>
              )}
            </div>

            {frequency === 'weekly' && (
              <div>
                <Label className="mb-1.5 block">Weekdays</Label>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAY_ORDER.map(wd => (
                    <button
                      key={wd}
                      type="button"
                      onClick={() => toggleWeekday(wd)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selectedWeekdays.includes(wd)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-emerald-300'
                      }`}
                      data-testid={`repeat-weekday-${wd}`}
                    >
                      {wd}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>End date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                data-testid="repeat-end-date-input"
              />
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2">
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={skipWeekends}
                  onChange={e => setSkipWeekends(e.target.checked)}
                  data-testid="repeat-skip-weekends-checkbox"
                />
                <span className="text-sm">Skip weekends (Sat + Sun)</span>
              </Label>

              <div>
                <Label className="text-sm block mb-1">Academy closure dates to skip</Label>
                <div className="flex gap-1.5">
                  <Input
                    type="date"
                    value={excludeDatesInput}
                    onChange={e => setExcludeDatesInput(e.target.value)}
                    data-testid="repeat-exclude-date-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addExcludeDate}
                    data-testid="repeat-add-exclude-date"
                  >
                    Add
                  </Button>
                </div>
                {excludeDates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {excludeDates.map(d => (
                      <Badge key={d} variant="secondary" className="gap-1 pr-1" data-testid={`repeat-exclude-chip-${d}`}>
                        {d}
                        <button
                          type="button"
                          onClick={() => removeExcludeDate(d)}
                          className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                          data-testid={`repeat-exclude-remove-${d}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {previewError && (
              <div className="text-xs text-red-600 flex items-center gap-1.5" data-testid="repeat-preview-error">
                <TriangleAlert className="w-3.5 h-3.5" />
                {previewError}
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Preview */}
        {step === 3 && preview && (
          <div className="space-y-3 py-2" data-testid="repeat-wizard-step-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 rounded-md p-3">
                <p className="text-[11px] uppercase font-semibold text-emerald-700 dark:text-emerald-300">Will create</p>
                <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200" data-testid="repeat-preview-will-create">
                  {preview.total_to_create}
                </p>
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400">sessions</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <p className="text-[11px] uppercase font-semibold text-gray-600 dark:text-gray-400">Skipped</p>
                <ul className="text-xs mt-1 space-y-0.5" data-testid="repeat-preview-skipped">
                  <li>
                    <span className="text-gray-500">Duplicates:</span>{' '}
                    <span className="font-semibold" data-testid="repeat-preview-dup-count">{preview.total_skipped_duplicates}</span>
                  </li>
                  <li>
                    <span className="text-gray-500">Excluded dates:</span>{' '}
                    <span className="font-semibold" data-testid="repeat-preview-excluded-count">{preview.total_skipped_excluded_dates}</span>
                  </li>
                  <li>
                    <span className="text-gray-500">Weekends:</span>{' '}
                    <span className="font-semibold" data-testid="repeat-preview-weekend-count">{preview.total_skipped_weekends}</span>
                  </li>
                </ul>
              </div>
            </div>

            {preview.exceeds_hard_limit && (
              <div className="text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2.5 flex items-start gap-2" data-testid="repeat-hard-limit-banner">
                <TriangleAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Above hard limit ({preview.hard_limit})</p>
                  <p>Split into multiple smaller repeat operations.</p>
                </div>
              </div>
            )}
            {!preview.exceeds_hard_limit && preview.requires_confirmation && (
              <div className="text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-2.5 flex items-start gap-2" data-testid="repeat-soft-warn-banner">
                <TriangleAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Large operation</p>
                  <p>You are about to create more than {preview.soft_warn_threshold} sessions. You&apos;ll be asked to confirm.</p>
                </div>
              </div>
            )}

            <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
              {preview.per_source.map(entry => (
                <div key={entry.source_session_id} className="p-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0" data-testid={`repeat-preview-source-${entry.source_session_id}`}>
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {entry.source_title} <span className="text-gray-400">· from {entry.source_date}</span>
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {entry.will_create.slice(0, 24).map(d => (
                      <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                        {d}
                      </span>
                    ))}
                    {entry.will_create.length > 24 && (
                      <span className="text-[10px] text-gray-500">+{entry.will_create.length - 24} more</span>
                    )}
                    {entry.will_create.length === 0 && (
                      <span className="text-[10px] text-gray-400 italic">Nothing to create for this source.</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
              Generated sessions start with attendance_state = scheduled. No rosters, marks or history are copied.
            </p>
          </div>
        )}

        <DialogFooter className="mt-2 gap-2">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={generating || previewLoading}
              data-testid="repeat-wizard-back"
            >
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              type="button"
              onClick={() => setStep(2)}
              disabled={!canGoStep2}
              data-testid="repeat-wizard-next-1"
            >
              Next: Pattern
            </Button>
          )}
          {step === 2 && (
            <Button
              type="button"
              onClick={fetchPreview}
              disabled={!canGoStep3 || previewLoading}
              data-testid="repeat-wizard-next-2"
            >
              {previewLoading ? 'Loading…' : 'Preview'}
            </Button>
          )}
          {step === 3 && (
            <Button
              type="button"
              onClick={runGenerate}
              disabled={generating || preview?.total_to_create === 0 || preview?.exceeds_hard_limit}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="repeat-wizard-generate"
            >
              {generating ? 'Generating…' : `Generate ${preview?.total_to_create ?? 0} sessions`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
